import { FuncionalidadesGeral } from './funcionalidades'

/**
 * Prefixo do produto como o realm publica a role de consulta de atendimentos:
 * `ouviidoria-func-consultar_atendimentos`, com dois "i". A grafia foi conferida contra as
 * 134 roles do client `ouvidoria` do realm de desenvolvimento em 22/09 e está documentada em
 * ComplementacaoAutorizacaoTest, no sgo-backend. Enquanto o realm não corrigir, esse é o nome
 * real da role, e o item sumia do menu do perfil Ouvidor mesmo com a permissão concedida.
 *
 * Tratar o prefixo errado como apelido do certo faz a funcionalidade valer com as duas
 * grafias, então a correção do realm não quebra nada e não é preciso uma entrada por
 * funcionalidade. Só o prefixo conhecido é aceito: o resto do identificador continua tendo de
 * casar.
 */
const PREFIXO_COM_ERRO_DE_GRAFIA = /^ouviidoria-/

/**
 * Identificador de funcionalidade comparável.
 *
 * Bug 872105: o GCA publica a mesma funcionalidade ora com hífen, ora com sublinhado
 * (`ouvidoria-func-consultar_atendimentos` e `ouvidoria_func_consultar_atendimentos` são a
 * mesma coisa), e a role que não casa letra a letra fazia o item desaparecer do menu mesmo
 * com a permissão concedida. Antes havia uma lista de apelidos, com uma entrada por
 * funcionalidade descoberta; normalizar o separador resolve a família inteira.
 *
 * Não afrouxa a verificação: o identificador tem de ser o mesmo, só o separador, a caixa e o
 * erro de grafia do prefixo do realm (ver PREFIXO_COM_ERRO_DE_GRAFIA) deixam de importar.
 * A ausência de colisão entre as funcionalidades de FuncionalidadesGeral depois da
 * normalização é garantida por teste.
 */
const idComparavel = (nome: string): string =>
  nome
    .toLowerCase()
    .replace(/[-_]+/g, '-')
    .trim()
    .replace(PREFIXO_COM_ERRO_DE_GRAFIA, 'ouvidoria-')

/** Verifica uma funcionalidade considerando o acesso total e o separador usado pelo GCA. */
export const hasPermission = (roles: string[] | undefined, permission: string): boolean => {
  if (!roles?.length) return false
  if (roles.includes(FuncionalidadesGeral.all) || roles.includes(permission)) return true

  const alvo = idComparavel(permission)
  const total = idComparavel(FuncionalidadesGeral.all)
  return roles.some((role) => {
    const atual = idComparavel(role)
    return atual === alvo || atual === total
  })
}

export const isSupervisorProfile = (profileName: string | undefined): boolean => {
  const normalized = profileName
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return normalized === 'supervisor' || normalized === 'perfil supervisor'
}

/**
 * Extrai todas as permissões de formulário do FuncionalidadesGeral
 */
export const getFormularioPermissions = (): string[] => {
  return [
    ...Object.values(FuncionalidadesGeral.dominios),
    ...Object.values(FuncionalidadesGeral.servicos),
    ...Object.values(FuncionalidadesGeral.assuntos),
    ...Object.values(FuncionalidadesGeral.metodos),
    ...Object.values(FuncionalidadesGeral.parametros),
    ...Object.values(FuncionalidadesGeral.valores),
    ...Object.values(FuncionalidadesGeral.associarSessaoPergunta),
    ...Object.values(FuncionalidadesGeral.associarFormulario),
    ...Object.values(FuncionalidadesGeral.secoesFormulario),
    ...Object.values(FuncionalidadesGeral.perguntas),
    ...Object.values(FuncionalidadesGeral.secoes),
    ...Object.values(FuncionalidadesGeral.formularios),
  ]
}

/**
 * Extrai todas as permissões de atendimento do FuncionalidadesGeral
 */
export const getAtendimentoPermissions = (): string[] => {
  return [
    ...Object.values(FuncionalidadesGeral.atendimento),
  ]
}

/**
 * Extrai todas as permissões de caixa de entrada do FuncionalidadesGeral
 */
export const getCaixaEntradaPermissions = (): string[] => {
  return [
    ...Object.values(FuncionalidadesGeral.caixaEntrada),
  ]
}

/**
 * Calcula o score de abrangência de um perfil baseado nas permissões
 */
export const calculateProfileScore = (profile: { roles: string[]; priority?: number }): number => {
  const formularioPermissions = getFormularioPermissions()
  const atendimentoPermissions = getAtendimentoPermissions()
  const caixaEntradaPermissions = getCaixaEntradaPermissions()

  // Mesma normalização do hasPermission (bug 872105): sem ela, o perfil cujas roles vêm com
  // o outro separador pontua zero e perde a escolha do perfil mais abrangente.
  const contar = (permissoes: string[]) => {
    const alvos = new Set(permissoes.map(idComparavel))
    return profile.roles.filter((role) => alvos.has(idComparavel(role))).length
  }

  const formularioCount = contar(formularioPermissions)
  const atendimentoCount = contar(atendimentoPermissions)
  const caixaEntradaCount = contar(caixaEntradaPermissions)
  const adminCount = profile.roles.filter(role => idComparavel(role) === idComparavel(FuncionalidadesGeral.all)).length

  // Score ponderado: admin tem peso maior, depois atendimento/caixa de entrada, depois formulário
  return (adminCount * 1000) + (atendimentoCount * 100) + (caixaEntradaCount * 100) + (formularioCount * 10) + (profile.priority || 0)
}
